import { moodColor, moodLabel } from '@adhd/shared';
import type { ThoughtRecord, VoiceLog } from '@adhd/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { TalkOffer } from '../components/TalkOffer.js';
import { NameEditor } from '../components/DayLifeEvent.js';
import { PickLegend } from '../components/WorksheetEditors.js';
import { DayPeopleRow } from '../components/DayPeopleRow.js';
import { api, ApiError, type OwnLifeEvent } from '../lib/api.js';
import { useAi } from '../lib/ai.js';
import { formatTime, whenWritten, writtenLate } from '../lib/mood.js';

/**
 * One journal entry.
 *
 * The interesting part is the bottom of this screen: taking three minutes of
 * unstructured talking and offering it back as a draft thought record. That is
 * the pairing this whole app is built around — speaking is what you can manage
 * when things are bad, and the structured record is what actually shifts
 * anything, and the gap between them is normally too much effort to cross.
 *
 * The draft is always shown for review and never saved on your behalf, and it
 * deliberately arrives with the balanced response left blank. A model filling
 * that in produces a tidy worksheet and no change.
 */
export function JournalEntry(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canSummarise, canConverse, turnedOff } = useAi();

  const [entry, setEntry] = useState<VoiceLog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);
  // Changing the subjects the app filed this entry under (18 Sep).
  const [editingTags, setEditingTags] = useState(false);
  // The list as it will read once saved, and every word on offer meanwhile: one
  // taken off stays in the row, unpressed, until Save changes — as a worksheet's
  // traps do.
  const [draftTags, setDraftTags] = useState<string[]>([]);
  const [tagChoices, setTagChoices] = useState<string[]>([]);
  const [typedTag, setTypedTag] = useState('');
  const [tagsBusy, setTagsBusy] = useState(false);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [audioFailed, setAudioFailed] = useState(false);
  const [linkedRecord, setLinkedRecord] = useState<ThoughtRecord | null>(null);
  const [offerAnswer, setOfferAnswer] = useState<'marked' | 'declined' | null>(null);
  const [offerBusy, setOfferBusy] = useState(false);
  const [marked, setMarked] = useState<OwnLifeEvent | null>(null);
  /*
   * Where the slider sits before anybody touches it — the model's proposal,
   * held in state rather than saved. Nothing is recorded until the button is
   * pressed, and moving it first is the ordinary case rather than a correction.
   */
  const [mood, setMood] = useState<number | null>(null);
  const [moodSaved, setMoodSaved] = useState(false);
  /*
   * Dismissed is not saved.
   *
   * These were one flag, so pressing Skip printed "That's in. Nothing else to
   * do." — the app telling you it had recorded a mood you had just declined to
   * give. Two flags, and Skip now simply closes the offer.
   */
  const [moodDismissed, setMoodDismissed] = useState(false);
  /*
   * Whether the number on the slider is his or the model's.
   *
   * Only meaningful when a suggestion existed: an untouched slider means he
   * accepted the model's reading, and a moved one means the number is his. With
   * no suggestion the slider starts empty, so any number at all is his.
   */
  const [moodTouched, setMoodTouched] = useState(false);
  const [moodBusy, setMoodBusy] = useState(false);
  /*
   * Today where he is, to decide whether a mood may still be given.
   *
   * `en-CA` is ISO, which is the format `day` is stored in — the same trick
   * Home.tsx and History.tsx use for the same comparison.
   */
  const todayKey = new Date().toLocaleDateString('en-CA');
  /*
   * The same grace the journal screen offers, honoured here.
   *
   * An entry filed to yesterday at 12:37 would otherwise have no way to carry a
   * mood: the offer is gated on the entry's day being today, so the writing
   * would land on Saturday and how he felt about it on Sunday — the exact split
   * this change exists to close.
   */
  const yesterdayKey = ((): string => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toLocaleDateString('en-CA');
  })();
  const stillOpen =
    entry?.day === todayKey || (new Date().getHours() < 4 && entry?.day === yesterdayKey);
  // A failed save used to vanish silently: no .catch, so the spinner stopped
  // and the section simply stayed put with no reason given.
  const [moodFailed, setMoodFailed] = useState(false);
  const [naming, setNaming] = useState(false);
  const pollRef = useRef<number | null>(null);

  const load = useCallback(async (): Promise<VoiceLog | null> => {
    if (!id) return null;
    try {
      const e = await api.getJournalEntry(id);
      setEntry(e);
      return e;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load that entry.");
      return null;
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Fetch the record this entry produced, so the link can show the thought
  // rather than a generic label. Which thought you pulled out of a long ramble
  // is the useful part, and it is what makes the pair legible as one thing.
  const linkedId = entry?.linkedThoughtRecordId ?? null;
  useEffect(() => {
    if (!linkedId) {
      setLinkedRecord(null);
      return;
    }
    let cancelled = false;
    api
      .getThoughtRecord(linkedId)
      .then((r) => !cancelled && setLinkedRecord(r))
      .catch(() => !cancelled && setLinkedRecord(null));
    return () => {
      cancelled = true;
    };
  }, [linkedId]);

  /*
   * Poll while anything is still arriving.
   *
   * Two separate background jobs land on this screen and the original only
   * waited for the first. Transcription is the visible one. Summarising runs
   * after it — and for a typed entry it is the ONLY one, because the text is
   * saved complete and the status is `done` from the first render. So a typed
   * entry settled instantly, the poll never started, and the summary appeared
   * only if you happened to reload the page. It had been written to the
   * database seconds after saving the whole time.
   *
   * Bounded by attempts rather than left open: if summarising failed, or the
   * entry was too short to be worth summarising, no summary is ever coming and
   * a screen that quietly polls forever is worse than one that stops.
   */
  /*
   * Identifies the FILE behind the player, not the entry.
   *
   * The URL never changes — it is keyed on the entry id — but what it serves
   * does. A recording is uploaded in whatever container the browser produced and
   * converted to mp3 seconds later, so the first thing this element loads is
   * often something the browser cannot play: Safari cannot decode the webm that
   * Chrome records.
   *
   * That is survivable. What was not is that the failure latched. `audioFailed`
   * was set on the error and cleared nowhere, and with an unchanged src React
   * kept the same element and the browser never retried — so the conversion
   * would finish, the poll would bring in the summary, and the player would go on
   * insisting the recording could not be played while a working mp3 sat behind
   * the exact URL it was already pointing at.
   *
   * Changing this key remounts the element, which is what forces a fresh load.
   */
  const audioKey = `${entry?.mimeType ?? ''}·${entry?.durationSeconds ?? ''}`;
  useEffect(() => {
    setAudioFailed(false);
  }, [audioKey]);

  // Seeded once, when the summary lands. Not kept in sync afterwards: once the
  // slider has been touched it belongs to the person, and a later poll
  // overwriting their position with the model's would be the app arguing.
  useEffect(() => {
    if (entry?.suggestedMood !== null && entry?.suggestedMood !== undefined) {
      setMood((m) => (m === null ? entry.suggestedMood : m));
    }
  }, [entry?.suggestedMood]);

  /*
   * One budget per phase, not one for the visit.
   *
   * There was a single `waits` counter shared by transcription and
   * summarising, capped at ten. A minute of speech takes far longer than ten
   * three-second polls to transcribe, so the budget was spent before the
   * write-up had even started — which made `awaitingSummary` false the instant
   * transcription finished. Two things followed: the spinner vanished at the
   * exact moment it should have changed its wording, and the poll stopped, so
   * the summary never arrived on screen at all. Leaving the entry and coming
   * back was the only way to see it, because that remounted with a fresh
   * counter.
   *
   * Transcription gets a long leash because the job is genuinely long and the
   * server drives it; summarising gets its own, starting from zero the moment
   * transcription lands.
   */
  const [transcriptWaits, setTranscriptWaits] = useState(0);
  const [summaryWaits, setSummaryWaits] = useState(0);
  const MAX_TRANSCRIPT_POLLS = 100; // five minutes
  const MAX_SUMMARY_POLLS = 40; // two
  const settled = entry ? entry.transcriptStatus !== 'pending' && entry.transcriptStatus !== 'processing' : false;
  /*
   * A summary that has not arrived in a quarter of an hour is not coming.
   *
   * This was inferred purely from the ABSENCE of a summary, with a counter that
   * resets on mount — so an entry whose write-up failed once showed "Reading it
   * back… the short version will appear here on its own" above his own words,
   * for ten polls, on every visit, for ever. The banner promised a thing that
   * was never going to happen, and did it most insistently on the entries where
   * something had gone wrong.
   *
   * Age is the honest test until there is a terminal summary status on the row
   * to read instead (docs/backlog.md). Fifteen minutes is far longer than the
   * job takes and short enough that nobody sits through it twice.
   */
  const recent =
    entry !== null && Date.now() - new Date(entry.recordedAt).getTime() < 15 * 60_000;
  const awaitingSummary = Boolean(
    entry && settled && !entry.summary && entry.transcript && recent &&
      summaryWaits < MAX_SUMMARY_POLLS,
  );
  // Conversion is a separate job from transcription and finishes on its own
  // schedule. When transcription is skipped — no AI provider, or an entry too
  // short — the status settles at once and nothing was left watching for the mp3
  // to appear, so the screen kept the uploaded file's failure indefinitely.
  const awaitingAudio = Boolean(
    entry?.hasAudio && entry.mimeType !== 'audio/mpeg' && summaryWaits < MAX_SUMMARY_POLLS,
  );

  useEffect(() => {
    if (!settled && transcriptWaits >= MAX_TRANSCRIPT_POLLS) {
      if (pollRef.current) window.clearTimeout(pollRef.current);
      return;
    }
    if (settled && !awaitingSummary && !awaitingAudio) {
      if (pollRef.current) window.clearTimeout(pollRef.current);
      return;
    }
    pollRef.current = window.setTimeout(() => {
      // Whichever phase is running is the one being counted.
      if (!settled) setTranscriptWaits((n) => n + 1);
      else setSummaryWaits((n) => n + 1);
      void load();
    }, 3000);
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [
    entry?.transcriptStatus,
    entry?.summary,
    settled,
    awaitingSummary,
    awaitingAudio,
    transcriptWaits,
    summaryWaits,
    load,
  ]);

  async function saveTags(next: string[]): Promise<void> {
    if (!id) return;
    setTagsBusy(true);
    setTagsError(null);
    try {
      setEntry(await api.updateJournalTags(id, next));
      setEditingTags(false);
      setTypedTag('');
    } catch (err) {
      setTagsError(err instanceof ApiError ? err.message : "Couldn't change those.");
    } finally {
      setTagsBusy(false);
    }
  }

  function addTag(): void {
    const word = typedTag.trim().toLowerCase();
    if (!word || draftTags.includes(word) || draftTags.length >= 12) return;
    setTypedTag('');
    setDraftTags([...draftTags, word]);
    setTagChoices((prev) => (prev.includes(word) ? prev : [...prev, word]));
  }

  async function makeDraft(): Promise<void> {
    if (!entry?.transcript) return;
    setDrafting(true);
    setDraftError(null);
    try {
      const result = await api.draftFromTranscript(entry.transcript, entry.id);
      if (result.draft) {
        // Opened as the worksheet itself, filled in, every step his to change —
        // not previewed here and saved as it stood (ThoughtRecordNew, DraftFromEntry).
        navigate('/thought-record', { state: { draft: result.draft, voiceLogId: entry.id } });
        return;
      }
      setDraftError(result.error ?? "Couldn't pull a thought out of that one.");
    } catch (err) {
      setDraftError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setDrafting(false);
    }
  }

  async function remove(): Promise<void> {
    if (!id) return;
    // Say what survives. Now that the two are shown as one thing, deleting the
    // entry looks like it might take the record with it.
    /*
     * Composed rather than branched, because there are now two independent
     * things that can survive this and the two-string version could only ever
     * mention one. Deleting an entry silently orphaned the life event it raised
     * — and, before the words were copied onto the event, silently destroyed
     * them too. Nothing is offered for deletion here: making somebody answer a
     * question about their bereavement because they tidied a recording is a
     * question at the worst possible moment.
     *
     * Deliberately does not quote the label or the span. This app has already
     * shipped an OS dialog repeating somebody's own sentence about the worst
     * thing in their year, and that is not being done twice.
     */
    const survives: string[] = [];
    if (entry?.linkedThoughtRecordId) survives.push('The thought record you made from it');
    if (entry?.lifeEventMarked) survives.push('What you marked as going on');
    const warning =
      'Delete this entry and its recording permanently?' +
      (survives.length > 0 ? ` ${survives.join(' and ')} will be kept.` : '') +
      ' This cannot be undone.';
    if (!confirm(warning)) return;
    // Not swallowed. A delete that fails and then navigates away as if it
    // worked is indistinguishable from one that succeeded, which is how a
    // broken DELETE went unnoticed.
    try {
      await api.deleteJournalEntry(id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete that entry.");
      return;
    }
    navigate('/days');
  }

  if (error) return <div className="banner banner-error">{error}</div>;
  if (!entry) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50dvh' }}>
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  const working = entry.transcriptStatus === 'pending' || entry.transcriptStatus === 'processing';

  // Both halves: switched on, and the entry actually read as a hard one.
  const showTalk = canConverse && entry.weight === 'heavy';

  /*
   * "Is this bigger than today?"
   *
   * NOTE WHAT THE MODEL CONTRIBUTED: the fact that it is asking, and a span
   * copied word for word out of the transcript and verified as a literal
   * substring on the server. Nothing it phrased itself appears here, and there
   * is no field to answer. Marking is one tap; naming is a separate step
   * afterwards that can be skipped forever.
   *
   * The quote is the difference between this and the version that was thrown
   * out. Asking "is something bigger going on?" seconds after being told
   * exactly what is going on reads as not having listened — but a paraphrase
   * would put "dying" in front of somebody who wrote "the vet rang", which is
   * the app naming their worst outcome before they have. Their own sentence
   * does neither.
   */
  const offerBlock =
    entry.lifeEventOffer && !offerAnswer ? (
      <div className="stack-sm stack">
        {/* Names the axis being asked about, which is what the generic version
            failed to do — and the word "today" retroactively says the offer
            above it was about tonight. Two questions, two time horizons, one
            block: heavy is a read of this evening that expires and saves
            nothing, a life event is a fact about the coming weeks that is
            stored and can be shared. */}
        <strong>Is this bigger than today?</strong>
        {entry.lifeEventOffer.quote && (
          <p className="offer-quote">“{entry.lifeEventOffer.quote}”</p>
        )}
        <p className="muted">
          Mark today as where it started and the days from here get read knowing it's there. Close
          it off whenever — the days it covered keep it. Nothing from this entry goes with it.
        </p>
        <div className="row">
          {/* Equal weight. A primary/ghost pair would make the no the harder
              press, which is the whole failure being avoided. */}
          <button
            className="btn btn-secondary grow"
            disabled={offerBusy}
            onClick={() => {
              setOfferBusy(true);
              void api
                .answerLifeEventOffer(entry.lifeEventOffer!.offerId, { accepted: true })
                .then((r) => {
                  setMarked(r.lifeEvent);
                  setOfferAnswer('marked');
                })
                .finally(() => setOfferBusy(false));
            }}
          >
            Mark it
          </button>
          <button
            className="btn btn-secondary grow"
            disabled={offerBusy}
            onClick={() => {
              setOfferBusy(true);
              void api
                .answerLifeEventOffer(entry.lifeEventOffer!.offerId, { accepted: false })
                .then(() => setOfferAnswer('declined'))
                .finally(() => setOfferBusy(false));
            }}
          >
            Not this
          </button>
        </div>
      </div>
    ) : offerAnswer === 'marked' ? (
      naming && marked ? (
        <NameEditor
          event={marked}
          suggested={entry.lifeEventOffer?.quote ?? null}
          onDone={(updated) => {
            if (updated) setMarked(updated);
            setNaming(false);
          }}
          onCancel={() => setNaming(false)}
        />
      ) : (
        <div className="stack-sm stack">
          {/* Closes the transaction and promises no follow-up chore. The
              obvious reply would have been filing instructions, which answers
              someone's worst news with admin.

              Once it HAS a name this has to stop offering to name it. It went
              on saying "a name for it if you want one — not now, or never"
              above a button reading "Give it a name", seconds after being
              given one, which reads as the app not having registered what just
              happened. */}
          <strong>Marked, from today.</strong>
          {marked?.label ? (
            <p className="muted">Filed as “{marked.label}”.</p>
          ) : (
            <p className="muted">A name for it if you want one — not now, or never.</p>
          )}
          {marked && (
            <button className="btn btn-ghost" onClick={() => setNaming(true)}>
              {marked.label ? 'Change the name' : 'Give it a name'}
            </button>
          )}
        </div>
      )
    ) : offerAnswer === 'declined' ? (
      /* Literally true: the offer row is stamped answered and this entry never
         raises it again. */
      <p className="muted">Alright. It won't ask again.</p>
    ) : null;

  const anyAppTag = entry.themes.some((t) => entry.appThemes.includes(t));

  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <button
          className="btn btn-ghost btn-compact"
          style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
          onClick={() => navigate('/days')}
        >
          <ArrowLeftIcon /> Back to log
        </button>
        {/* The DAY it belongs to, not the instant it was typed. For anything
            filed under the late-night grace those are different days, and the
            heading of an entry about Saturday should say Saturday. */}
        <h1>{entry.title ?? formatDate(`${entry.day}T12:00:00`)}</h1>
        {/* The date only when it is not already the heading. With no title the
            page opened with the same date twice, at 28px and again at 16px
            directly under it — the normal state at `transcribe` and `off`,
            where nothing writes a title. */}
        {(entry.title || entry.durationSeconds) && (
          <p>
            {entry.title ? formatDate(`${entry.day}T12:00:00`) : ''}
            {entry.durationSeconds
              ? `${entry.title ? ' · ' : ''}${formatDuration(entry.durationSeconds)}`
              : ''}
          </p>
        )}
        {/*
          Said outright when the two facts differ.
          
          `day` and `recorded_at` are the same day for almost every entry, and
          for one written at 3am about the day that just ended they are not.
          Showing only the clock time makes an entry written on Sunday look like
          it happened at 3am on Saturday — the wrong day, and the wrong feeling
          about the day.
        */}
        {writtenLate(entry.day, entry.recordedAt) && (
          <p className="faint">Written {whenWritten(entry.recordedAt)}, for the day before.</p>
        )}
      </header>

      {entry.hasAudio && (
        <div className="stack-sm stack">
          <audio
            // Remounts when the file behind the URL changes — see audioKey.
            // Without it the element survives the conversion finishing and never
            // asks for the version it can actually play.
            key={audioKey}
            controls
            preload="metadata"
            src={`/api/voice-logs/${entry.id}/audio`}
            style={{ width: '100%' }}
            onError={() => setAudioFailed(true)}
          >
            Your browser can't play audio.
          </audio>
          {/* Recordings are converted to MP3 on the server, so this should now
              be rare — it means the conversion did not run or did not finish.
              Don't guess at the cause in the copy; say what's true and give a
              way out. The server retries conversion on its next sweep, so
              reloading later may well fix it on its own. */}
          {audioFailed && awaitingAudio && (
            <div className="banner">
              <span className="spinner" style={{ display: 'inline-block', verticalAlign: -4 }} />{' '}
              Still converting this recording so it plays everywhere. It will start working on its
              own in a moment.
            </div>
          )}
          {/* Only once conversion has actually had its go. Saying "this browser
              can't play it" while the file is still being converted blames the
              browser for something that has not finished happening. */}
          {audioFailed && !awaitingAudio && (
            <div className="banner banner-caution">
              This browser can't play that recording. The transcript below is the same content, and
              the server may still be converting the audio — it's worth trying again in a minute.{' '}
              {/* `.linkish`, or it renders in the browser's own link blue — the
                  one unstyled anchor left in the app. */}
              <a className="linkish" href={`/api/voice-logs/${entry.id}/audio`} download>
                Download the file
              </a>{' '}
              to play it elsewhere.
            </div>
          )}
        </div>
      )}

      {working && (
        <div className="banner">
          <span className="spinner" style={{ display: 'inline-block', verticalAlign: -4 }} />{' '}
          Turning this into words… it'll appear here on its own. You can leave and come back.
        </div>
      )}

      {/* The SECOND wait, which had no indicator at all.
          Transcribing and summarising are separate jobs, and only the first one
          said anything — so a typed entry, whose transcript is complete the
          instant it is saved, sat there looking finished while the write-up was
          still being made, and then the summary appeared out of nowhere.
          `awaitingSummary` was already computed and already driving the poll;
          nothing rendered it. */}
      {!working && awaitingSummary && canSummarise && (
        <div className="banner">
          <span className="spinner" style={{ display: 'inline-block', verticalAlign: -4 }} /> Reading
          it back… the short version will appear here on its own.
        </div>
      )}

      {entry.transcriptStatus === 'failed' && (
        <div className="banner banner-caution stack-sm stack">
          <span>That didn't transcribe. {entry.transcriptError}</span>
          <button
            className="btn btn-secondary"
            onClick={() => void api.retranscribe(entry.id).then(() => load())}
          >
            Try again
          </button>
        </div>
      )}

      {entry.transcriptStatus === 'skipped' && (
        <div className="banner">
          {/* `skipped` covers two different situations and used to describe only
              one of them. Telling somebody a feature "isn't set up" when they
              switched it off themselves explains their own decision back to
              them as a fault. */}
          {turnedOff
            ? "Saved as audio. AI is off, so there's no written version."
            : "Saved as audio. Transcription isn't set up on this server, so there's no written version."}
        </div>
      )}

      {/* Who was in the day — release 2 of 2.0. Outside the summary's
          conditional on purpose: with AI off there is no summary and no app
          pick, and the row is still where he adds someone by hand. */}
      <DayPeopleRow day={entry.day} voiceLogId={entry.id} />

      {entry.summary && (
        <section className="stack-sm stack">
          <h2 className="section-head">The short version</h2>
          <div className="card">
            <p className="reading">{entry.summary}</p>
            {/* The subjects it filed this entry under, worked exactly like a
                worksheet's thinking traps: the app's picks dashed, his solid,
                the key beside them, and a tap only un-picks — nothing is gone
                until Save changes. They were plain text with no route behind
                them until 18 Sep. */}
            <div className="stack-sm stack" style={{ marginTop: 'var(--space-3)' }}>
              {anyAppTag && (
                <PickLegend
                  appLabel="the app’s pick"
                  note={editingTags ? 'Tap one to take it off, then save.' : ''}
                />
              )}
              <div className="chips">
                {(editingTags ? tagChoices : entry.themes).map((t) => {
                  const app = entry.appThemes.includes(t);
                  const on = editingTags ? draftTags.includes(t) : true;
                  const cls = app && on ? 'chip chip-app' : 'chip';
                  return editingTags ? (
                    <button
                      key={t}
                      type="button"
                      className={cls}
                      aria-pressed={on}
                      aria-label={app ? `${t} (the app's pick)` : undefined}
                      disabled={tagsBusy}
                      onClick={() =>
                        setDraftTags(on ? draftTags.filter((x) => x !== t) : [...draftTags, t])
                      }
                    >
                      {t}
                    </button>
                  ) : (
                    <span className={cls} key={t} style={{ cursor: 'default' }}>
                      {t}
                    </span>
                  );
                })}
              </div>
              {!editingTags && entry.themes.length > 0 && (
                <button
                  type="button"
                  className="chips-change"
                  onClick={() => {
                    setDraftTags(entry.themes);
                    setTagChoices(entry.themes);
                    setEditingTags(true);
                  }}
                >
                  Change these
                </button>
              )}
              {editingTags && (
                <>
                  <div className="row" style={{ gap: 'var(--space-2)' }}>
                    <label htmlFor="own-tag" className="sr-only">
                      Another subject, in your words
                    </label>
                    <input
                      id="own-tag"
                      className="input"
                      style={{ flex: 1, minWidth: 0 }}
                      value={typedTag}
                      maxLength={40}
                      placeholder="Another word for it"
                      disabled={tagsBusy}
                      onChange={(e) => setTypedTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={addTag}
                      disabled={tagsBusy || !typedTag.trim() || draftTags.length >= 12}
                    >
                      Add
                    </button>
                  </div>
                  <div className="stack-sm stack">
                    <button
                      className="btn btn-primary btn-block"
                      onClick={() => void saveTags(draftTags)}
                      disabled={tagsBusy}
                    >
                      {tagsBusy ? <span className="spinner" /> : 'Save changes'}
                    </button>
                    <button
                      className="btn btn-ghost btn-block"
                      onClick={() => {
                        setEditingTags(false);
                        setTypedTag('');
                        setTagsError(null);
                      }}
                      disabled={tagsBusy}
                    >
                      Keep them as they are
                    </button>
                  </div>
                </>
              )}
              {tagsError && <div className="banner banner-caution">{tagsError}</div>}
            </div>
          </div>
        </section>
      )}

      {/* Under the summary, because the summary is where the judgement came
          from — and above the transcript, so it is not buried under a wall of
          your own words. In `button` mode nothing has read anything and the
          control is simply always there, which is why the weight is not
          consulted on that branch. */}
      {/* One block, not two.
          Both offers fire from the same entry for the same reason, and they
          were previously separate cards with the whole transcript between them
          — which on the night you learn something reads as the app asking twice
          about one thing. Talking is offered first: company before filing. */}
      {showTalk ? (
        <TalkOffer reason={{ kind: 'weight' }} occurredAt={entry.recordedAt} footer={offerBlock} />
      ) : (
        offerBlock && <div className="talk-offer stack-sm stack">{offerBlock}</div>
      )}

      {entry.transcript && (
        <section className="stack-sm stack">
          {/* An entry can be spoken or typed; "What you said" over something
              somebody typed is a small wrongness that makes the app feel like
              it isn't paying attention. */}
          <h2 className="section-head">
            {entry.hasAudio ? 'What you said' : 'What you wrote'}
          </h2>
          <div className="card">
            <p className="reading" style={{ whiteSpace: 'pre-wrap' }}>
              {entry.transcript}
            </p>
          </div>
        </section>
      )}

      {/* No second render of offerBlock here. It is placed once, up beside the
          talk offer — either inside that block or standing alone in its own.
          Leaving the original site in as well put "Something bigger going on?"
          on the screen twice, ten centimetres apart. */}

      {/* --- How you were feeling ------------------------------------------
          The pairing the owner asked for: "a journal entry should always
          capture how im feeling, but maybe how im feeling doesn't always need
          a journal". Talking for two minutes about an afternoon and then being
          asked to rate the same afternoon is the app failing to listen twice.

          PRE-PLACED, NOT SAVED. The slider opens where the entry sounded, and
          nothing is recorded until the button is pressed. That is the same rule
          that stops a drafted thought record inventing an emotion intensity,
          and it matters more here: a number nobody gave looks identical to one
          they did once it is in a trend chart, and that chart is read for
          patterns and handed to clinicians. */}
      {/*
        ALWAYS OFFERED, AT EVERY AI SETTING.
        
        This was gated on `entry.suggestedMood !== null`, and `suggested_mood`
        is written only when the rung reaches `reflect`
        (transcription-worker.ts:293). So at off, transcribe and summarise the
        whole block was absent and there was no way, anywhere in the app, to say
        how you felt about something you had just written. Migration 021 shipped
        to deliver exactly that, quoting the sentence asking for it.
        
        It also inverted the standing rule. The model governs what gets MADE — a
        suggestion — and never what gets SHOWN. Here a missing suggestion was
        deleting the offer.
        
        So the control does not appear and disappear with the ladder. The only
        thing the rung changes is where the slider starts and one sentence of
        copy.
      */}
      {/*
        AND ONLY WHILE THE DAY IS STILL OPEN.
        
        This offer used to appear on an entry of any age, and the save posts no
        recordedAt — so the server dated the check-in `now` (mood.ts) while the
        entry kept its own frozen day. Proved on a 25-day-old entry: the mood
        landed on today, as a card with nothing in it, and joined today's mean,
        today's streak and any share link covering today. A feeling about the
        10th of August counted as the 4th of September.
        
        Filing it on the entry's day instead would move a settled day's figures
        under sentences that are frozen, which is the trade he rejected for
        deletions an hour before this shipped. Same rule, same answer: the day
        it happened, and then the day is finished.
        
        And there is a truer reason than consistency. A mood given three weeks
        later is not a record of how he felt then; it is how he feels now about
        then, and nothing in this schema can tell those two apart.
      */}
      {!entry.moodCheckin && !moodSaved && !moodDismissed && stillOpen && (
        <section className="card stack">
          <h3>{entry.suggestedMood !== null ? 'How were you feeling?' : 'How you were feeling'}</h3>
          <p className="muted">
            {entry.suggestedMood !== null
              ? 'From what you said, this sounded about here. Move it if that is not right — it is not saved until you say so.'
              : 'Add it if you want to — the entry is saved either way.'}
          </p>
          <input
            className="slider"
            type="range"
            min={-5}
            max={5}
            step={1}
            value={mood ?? 0}
            // `input` rather than `change`, the same fix CheckIn.tsx:158 carries:
            // dragging to the value the slider already sits on fires no change
            // event. With no suggestion it sits at zero, and zero — Neutral — is
            // a real answer somebody may well be reaching for.
            onInput={(e) => {
              setMoodTouched(true);
              setMood(Number((e.target as HTMLInputElement).value));
            }}
          />
          <div className="slider-scale">
            <span className="faint">Awful</span>
            <strong
              style={{
                fontSize: 'var(--text-lg)',
                color: mood === null ? 'var(--text-muted)' : moodColor(mood),
              }}
            >
              {/* Not a word, and not a number, until one is given. Showing
                  "Neutral" under an untouched slider is the app putting an
                  answer in his mouth — the same argument CheckIn.tsx:32-52 makes
                  about its own default. */}
              {mood === null ? 'Not said yet' : moodLabel(mood)}
            </strong>
            <span className="faint">Great</span>
          </div>
          <div className="row">
            <button
              className="btn btn-secondary grow"
              disabled={moodBusy || mood === null}
              onClick={() => {
                if (mood === null) return;
                setMoodBusy(true);
                void api
                  .createCheckin({
                    mood,
                    energy: null, focus: null, restlessness: null, overwhelm: null,
                    irritability: null, rejectionSensitivity: null, anxiety: null,
                    contextTags: [], note: null,
                    // Recorded as what it is. A check-in confirmed from an entry
                    // is not the same act as one made from scratch, and the
                    // record should be able to tell them apart later.
                    //
                    // `ai_assisted` only when the number really is the model's,
                    // accepted untouched. A number he dragged is his, whatever
                    // suggested it, and below `reflect` nothing suggested
                    // anything — calling those ai_assisted would file his own
                    // answer under the model's name.
                    source: entry.suggestedMood !== null && !moodTouched ? 'ai_assisted' : 'manual',
                    voiceLogId: entry.id,
                    // The feeling belongs to the day the writing does.
                    day: entry.day,
                  })
                  .then(() => setMoodSaved(true))
                  .catch(() => setMoodFailed(true))
                  .finally(() => setMoodBusy(false));
              }}
            >
              {moodBusy ? <span className="spinner" /> : 'Save this'}
            </button>
            <button className="btn btn-ghost" onClick={() => setMoodDismissed(true)}>
              Not now
            </button>
          </div>
          {moodFailed && (
            <p className="faint">That did not save. The entry itself is safe.</p>
          )}
        </section>
      )}

      {/*
        THE MOOD THIS ENTRY CARRIES, STATED AND KEPT.
        
        This was a banner reading "That's in. Nothing else to do." that appeared
        once, on the save, and was gone when the screen was next opened. So the
        link that migration 021 stores was drawn nowhere at all: the entry never
        said which mood it had, and the mood never said which entry it came
        from. The banner was the app confirming a write, which is not the same
        as the entry knowing something about itself.
        
        Now it is a line that is simply true whenever it is true. `moodSaved`
        only carries the moment between the save and the refetch.
      */}
      {(entry.moodCheckin || moodSaved) && (
        <section className="stack-sm stack">
          <h2 className="section-head">How you were feeling</h2>
          <div className="card">
            <div className="row-between">
              <strong style={{ color: moodColor(entry.moodCheckin?.mood ?? mood ?? 0) }}>
                {moodLabel(entry.moodCheckin?.mood ?? mood ?? 0)}
              </strong>
              <span className="faint">
                {entry.moodCheckin ? formatTime(entry.moodCheckin.recordedAt) : 'just now'}
              </span>
            </div>
            {/* Only when the number really is the model's, accepted untouched.
                Below `reflect` nothing suggests anything, so this never shows
                there — which is the honest behaviour, not a missing feature. */}
            {entry.moodCheckin?.source === 'ai_assisted' && (
              <p className="faint" style={{ margin: 0 }}>
                Suggested from what you wrote, and you kept it.
              </p>
            )}
          </div>
        </section>
      )}

      {/* --- The record this entry became ---------------------------------- */}
      {/* Once a record exists, this entry is half of a pair and should say so.
          Previously the screen went on offering "Have a look" as though nothing
          had happened, which both hid the link and invited a second, duplicate
          record off the same transcript. */}
      {entry.linkedThoughtRecordId && (
        <section className="stack-sm stack">
          <h2 className="section-head">What you did with this</h2>
          <Link className="option" to={`/thought-record/${entry.linkedThoughtRecordId}`}>
            <strong>
              {linkedRecord ? `“${linkedRecord.automaticThought}”` : 'The thought record you made'}
            </strong>
            <span>
              {linkedRecord && !linkedRecord.isComplete
                ? 'You pulled this thought out of the entry, but haven’t answered it yet.'
                : 'The thought you pulled out of this, and what you said back to it.'}
            </span>
          </Link>
        </section>
      )}

      {/* --- Draft a thought record from this ------------------------------ */}
      {/* Drafting restructures what you wrote into the shape of a record. It
          never forms a view about how you are doing, so it sits at `summarise`
          rather than `reflect` — but it is still a model rewriting your words,
          so it goes when the ladder says it goes. */}
      {entry.transcript && canSummarise && !entry.linkedThoughtRecordId && (
        <section className="card stack">
          <h3>Is there a thought in here worth working on?</h3>
          <p className="muted">
            This pulls what you said into the shape of a thought record — the situation, the feeling,
            the thought underneath. It opens as a worksheet with each step filled in, for you to check and change before
            anything is saved. It won't write the part where you answer the thought; that bit only works if it's yours.
          </p>
          {draftError && <div className="banner banner-caution">{draftError}</div>}
          <button
            className="btn btn-primary btn-block"
            onClick={() => void makeDraft()}
            disabled={drafting}
          >
            {drafting ? <span className="spinner" /> : 'Have a look'}
          </button>
        </section>
      )}

      <button className="btn btn-ghost btn-ghost-quiet btn-block" onClick={() => void remove()}>
        Delete this entry
      </button>
    </div>
  );
}

/* Same words and colours as the check-in screen, so one afternoon does not get
   two different vocabularies depending on which way in you took. */


function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function formatDuration(total: number): string {
  const m = Math.floor(total / 60);
  const s = Math.round(total % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
